import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { useCookies } from 'react-cookie';
import server from '../services/server';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    AUTH_COOKIE_PATHS,
    AUTH_SESSION_EVENT,
    clearAuthSession,
    consumeSupportHandoff,
    getAuthToken,
    getStoredUser,
    mergeSessionUser,
    migrateAuthFromCookies,
    persistAuthSession,
    pickSessionUser,
    isAdvertiserContractSigned,
} from '../services/authSession';
import {
    canAccessAdvertiserPath,
    getAdvertiserHomePath,
    isAdvertiserAttendant,
} from '../modules/Advertiser/utils/advertiserPermissions';

const AuthContext = createContext({});

export const AuthProvider = props => {
    const [, , removeCookie] = useCookies(['token', 'refreshtoken', 'user', 'club', 'advertiser']);
    const [authenticated, setAuthenticated] = useState(() => {
        consumeSupportHandoff();
        return Boolean(getAuthToken());
    });
    const [authReady, setAuthReady] = useState(false);
    const [user, setUser] = useState(() => getStoredUser());
    const navigation = useNavigate();
    const location = useLocation();
    const refreshingProfileRef = useRef(false);

    const clearLegacyCookies = useCallback(() => {
        const keys = ['token', 'refreshtoken', 'user', 'club', 'advertiser'];
        try {
            AUTH_COOKIE_PATHS.forEach(path => keys.forEach(key => removeCookie(key, { path })));
        } catch (e) {
            console.warn('Logout cookie removal warning:', e);
        }
    }, [removeCookie]);

    const LogOut = useCallback(() => {
        clearAuthSession();
        clearLegacyCookies();
        delete server.defaults.headers.Authorization;
        setAuthenticated(false);
        setUser(null);
    }, [clearLegacyCookies]);

    const syncTokenFromStorage = useCallback(() => {
        const token = getAuthToken();
        if (token) {
            server.defaults.headers['Authorization'] = `Bearer ${token}`;
            setAuthenticated(true);
            return true;
        }

        delete server.defaults.headers.Authorization;
        setAuthenticated(false);
        return false;
    }, []);

    /** Atualiza cargo/perfil via GET /me (fonte viva no banco). */
    const refreshSessionProfile = useCallback(async () => {
        if (!getAuthToken() || refreshingProfileRef.current) return null;
        refreshingProfileRef.current = true;
        try {
            const { data } = await server.get('/me');
            const nextUser = data?.data;
            if (!nextUser) return null;

            let merged = null;
            setUser(prev => {
                merged = mergeSessionUser(prev, nextUser);
                return merged;
            });
            return merged;
        } catch (error) {
            console.log(error);
            return null;
        } finally {
            refreshingProfileRef.current = false;
        }
    }, []);

    useEffect(() => {
        migrateAuthFromCookies();

        const storedUser = getStoredUser();
        if (storedUser) {
            setUser(storedUser);
        }

        if (syncTokenFromStorage()) {
            refreshSessionProfile();
        }

        setAuthReady(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Revalida cargo ao navegar no anunciante e ao focar a aba.
    useEffect(() => {
        if (!authenticated) return undefined;

        if (location.pathname.startsWith('/anunciante')) {
            refreshSessionProfile();
        }

        const onFocus = () => {
            if (document.visibilityState === 'visible') {
                refreshSessionProfile();
            }
        };

        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onFocus);
        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onFocus);
        };
    }, [authenticated, location.pathname, refreshSessionProfile]);

    // Se o cargo mudou e a rota ficou bloqueada, redireciona.
    useEffect(() => {
        if (!user || user.profile !== 'advertiser') return;
        if (!location.pathname.startsWith('/anunciante')) return;
        if (
            location.pathname.startsWith('/anunciante/login') ||
            location.pathname.startsWith('/anunciante/contrato') ||
            location.pathname.startsWith('/anunciante/convite') ||
            location.pathname.startsWith('/anunciante/registro')
        ) {
            return;
        }

        if (!canAccessAdvertiserPath(user, location.pathname)) {
            navigation(getAdvertiserHomePath(user), { replace: true });
        }
    }, [user, location.pathname, navigation]);

    useEffect(() => {
        const handleAuthSessionEvent = event => {
            const { detail } = event;
            if (!detail) return;

            if (detail.logout) {
                LogOut();
                const loginPath = location.pathname.startsWith('/clube')
                    ? '/clube/login'
                    : location.pathname.startsWith('/anunciante')
                      ? '/anunciante/login'
                      : '/';
                navigation(loginPath, { replace: true });
                return;
            }

            if (detail.token) {
                persistAuthSession({
                    token: detail.token,
                    refreshtoken: detail.refreshtoken,
                });
                server.defaults.headers['Authorization'] = `Bearer ${detail.token}`;
                setAuthenticated(true);
                refreshSessionProfile();
            }
        };

        window.addEventListener(AUTH_SESSION_EVENT, handleAuthSessionEvent);
        return () => window.removeEventListener(AUTH_SESSION_EVENT, handleAuthSessionEvent);
    }, [LogOut, location.pathname, navigation, refreshSessionProfile]);

    const Login = async ({ token, refreshtoken, user: loginUser, club, advertiser }) => {
        server.defaults.headers['Authorization'] = `Bearer ${token}`;

        const sessionUser = pickSessionUser(loginUser);

        persistAuthSession({
            token,
            refreshtoken,
            user: sessionUser,
            club,
            advertiser,
        });

        clearLegacyCookies();

        setAuthenticated(true);
        setUser(sessionUser);

        const freshUser = (await refreshSessionProfile()) || sessionUser;

        if (club) {
            navigation('/clube', { replace: true });
        } else if (advertiser) {
            if (isAdvertiserAttendant(freshUser)) {
                navigation(getAdvertiserHomePath(freshUser), { replace: true });
            } else if (!isAdvertiserContractSigned(advertiser)) {
                navigation('/anunciante/contrato', { replace: true });
            } else {
                navigation(getAdvertiserHomePath(freshUser), { replace: true });
            }
        } else {
            navigation('/dashboard', { replace: true });
        }
    };

    return (
        <AuthContext.Provider
            value={{
                authenticated,
                authReady,
                user,
                LogOut,
                Login,
                refreshSessionProfile,
            }}
        >
            {props.children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw 'Use Auth Context inside Auth Provider';

    return context;
};
